import { useCallback, useEffect, useRef, useState } from 'react';

interface Position {
  x: number;
  y: number;
}

interface DragState {
  isDragging: boolean;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
}

interface UseDraggableReturn {
  position: Position;
  onPointerDown: (e: React.PointerEvent) => void;
  resetPosition: () => void;
}

/**
 * Generic hook for making any element draggable via pointer events.
 *
 * Uses `transform: translate(x, y)` exclusively — never mutates `top`/`left`.
 * Tracks drag state in refs to avoid re-renders during drag.
 *
 * @param enabled - Whether dragging is enabled. When false, `onPointerDown` is a no-op.
 * @returns `{ position, onPointerDown, resetPosition }`
 *
 * @example
 * ```tsx
 * const { position, onPointerDown, resetPosition } = useDraggable(true);
 *
 * return (
 *   <div
 *     style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
 *     onPointerDown={onPointerDown}
 *   >
 *     Drag me
 *   </div>
 * );
 * ```
 */
export const useDraggable = (enabled: boolean): UseDraggableReturn => {
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const dragState = useRef<DragState>({
    isDragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  const resetPosition = useCallback(() => {
    setPosition({ x: 0, y: 0 });
  }, []);

  const handlePointerMove = useCallback((e: PointerEvent) => {
    if (!dragState.current.isDragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setPosition({
      x: dragState.current.originX + dx,
      y: dragState.current.originY + dy,
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    dragState.current.isDragging = false;
    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
  }, [handlePointerMove]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      dragState.current = {
        isDragging: true,
        startX: e.clientX,
        startY: e.clientY,
        originX: position.x,
        originY: position.y,
      };
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
    },
    [enabled, position.x, position.y, handlePointerMove, handlePointerUp],
  );

  useEffect(() => {
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  return { position, onPointerDown, resetPosition };
};
